import type { JsonSchema } from '@/lib/data-extraction';

export interface SentimentTagFormValue {
  id: string;
  label: string;
  description: string;
}

const createTagId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `sentiment-tag-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const createEmptySentimentTag = (
  overrides?: Partial<SentimentTagFormValue>
): SentimentTagFormValue => ({
  id: createTagId(),
  label: '',
  description: '',
  ...overrides,
});

// Starter tags so the editor is prefilled with the classic sentiment buckets while still
// letting users replace them with labels that fit their own use case.
export const SENTIMENT_DEFAULT_TAGS: SentimentTagFormValue[] = [
  createEmptySentimentTag({
    label: 'POSITIVE',
    description: 'The caller expresses satisfaction, agreement, or positive emotion.',
  }),
  createEmptySentimentTag({
    label: 'NEUTRAL',
    description: 'The caller is factual or shows no strong emotion either way.',
  }),
  createEmptySentimentTag({
    label: 'NEGATIVE',
    description: 'The caller expresses dissatisfaction, frustration, or negative emotion.',
  }),
];

const LABEL_DESCRIPTION_INTRO =
  "Classify the caller's sentiment as exactly one of the following labels:";

// Renders the configured tags into the label field's `description` so the model receives
// the meaning of each tag alongside the enum constraint. The format is parsed back by
// `mapJsonSchemaToSentimentTags` to round-trip descriptions through the saved schema.
const buildLabelDescription = (tags: SentimentTagFormValue[]) => {
  const lines = tags.map(tag => {
    const description = tag.description.trim();

    return description ? `- ${tag.label}: ${description}` : `- ${tag.label}`;
  });

  return [LABEL_DESCRIPTION_INTRO, ...lines].join('\n');
};

// Builds the structured-output schema for the SENTIMENT post-call prompt from the
// user-defined tags. `label` is constrained to the configured tag names; `score`
// (range [-1, 1]) and `rationale` remain optional.
export const buildSentimentJsonSchema = (
  tags: Array<Partial<SentimentTagFormValue>> = []
): JsonSchema => {
  const normalizedTags = tags
    .map(tag => createEmptySentimentTag({ ...tag, label: (tag.label ?? '').trim() }))
    .filter(tag => tag.label);

  return {
    type: 'object',
    properties: {
      label: {
        type: 'string',
        enum: normalizedTags.map(tag => tag.label),
        description: buildLabelDescription(normalizedTags),
      },
      score: { type: 'number', minimum: -1, maximum: 1 },
      rationale: { type: 'string' },
    },
    required: ['label'],
    additionalProperties: false,
  };
};

// Recovers the editable tag list from a previously saved sentiment schema. Labels come
// from the `label` enum (the source of truth); descriptions are parsed from the matching
// `- <label>: <description>` lines produced by `buildLabelDescription`.
export const mapJsonSchemaToSentimentTags = (
  schema?: JsonSchema | null
): SentimentTagFormValue[] => {
  const labelSchema = schema?.properties?.label;
  const labels = (labelSchema?.enum ?? [])
    .map(value => (value === null ? '' : String(value).trim()))
    .filter(Boolean);

  if (labels.length === 0) {
    return [];
  }

  const descriptionByLabel = new Map<string, string>();

  (labelSchema?.description ?? '')
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.startsWith('- '))
    .forEach(line => {
      const entry = line.slice(2);
      const separatorIndex = entry.indexOf(': ');
      const label = (separatorIndex >= 0 ? entry.slice(0, separatorIndex) : entry).trim();
      const description = separatorIndex >= 0 ? entry.slice(separatorIndex + 2).trim() : '';

      if (label) {
        descriptionByLabel.set(label, description);
      }
    });

  return labels.map(label =>
    createEmptySentimentTag({
      label,
      description: descriptionByLabel.get(label) ?? '',
    })
  );
};
